import React, { useState, useRef, useEffect, lazy, Suspense, useCallback } from 'react';
import { useAutoSave } from '../../hooks/useAutoSave';
import AutoSaveIndicator from '../../composants/AutoSaveIndicator/AutoSaveIndicator';
import AlertMessage from './components/AlertMessage';
import Button from '../../composants/Button/Button';
import TabNav from '../../composants/TabNav/TabNav';
import ProfilSkeleton from '../../composants/Skeleton/ProfilSkeleton';
import ChangeRole from '../../composants/ChangeRole/ChangeRole';
import MesDocuments from '../../composants/MesDocuments/MesDocuments';
import GestionComptes from '../../composants/GestionComptes/GestionComptes';
import GestionComptesPrestataires from '../../composants/GestionComptesPrestataires/GestionComptesPrestataires';
import { useAuth } from '../../contexts/AuthContext'; // Ajout de l'import pour accéder au contexte d'authentification
import { useSearchParams } from 'react-router-dom';
import {
  isClientRole,
  isPrestataireRole,
  isAdminRole
} from '../../utils/roleUtils'; // Import des fonctions de utils/roleUtils
import './Profil.css';
import './ProfilFormHarmonise.css';
import ModificationModal from '../../composants/ModificationModal/ModificationModal';
import TabDocumentsAcheteur from './tabs/TabDocumentsAcheteur';
import { useAcheteurProfile } from '../../contexts/AcheteurProfileContext';
import ProgressionBar from './components/ProgressionBar';
import iconLocation from '../../assets/statistiques/icons/location.svg';
import iconBudget from '../../assets/statistiques/icons/budget.svg';
import iconTrendingUp from '../../assets/statistiques/icons/trending-up.svg';
import iconAmount from '../../assets/statistiques/icons/amount.svg';
import iconOrders from '../../assets/statistiques/icons/orders.svg';
import iconDistribution from '../../assets/statistiques/icons/distribution.svg';
import iconActiveUsers from '../../assets/statistiques/icons/active-users.svg';
import EvaluationCentralis, { EvaluationCentralisData, EvalCritere, EvaluationLastUpdateBanner } from '../../composants/EvaluationCentralis/EvaluationCentralis';
import { pdf } from '@react-pdf/renderer';
import AvisPDF from '../PDF/AvisPDF';
import { ReactComponent as PdfIcon } from '../../assets/icons/pdf.svg';
import AvisGroupes from '../../composants/AvisPrestataire/AvisGroupes';
import { AvisData } from '../../composants/AvisPrestataire/avisTypes';
import EstimationBesoins, { DerniereMaj } from '../../composants/EstimationBesoins/EstimationBesoins';
import LocalisationBesoins from '../../composants/LocalisationBesoins/LocalisationBesoins';
import { buildStatsBesoinsCsv } from '../../utils/statsBesoinsCsv';
import { departementFromCodePostal } from '../../utils/departement';
import { adresseUneLigne, ligneVoie } from '../../utils/adresse';
import Banner from '../../composants/Banner/Banner';
import SectionPliable from '../../composants/SectionPliable/SectionPliable';
import PerimetreSelector, { PerimetreValue } from '../../composants/PerimetreSelector/PerimetreSelector';
import PerimetreStatCard from '../../composants/PerimetreSelector/PerimetreStatCard';
import { apiService } from '../../services/apiService';

// Reponse de GET /synthese-besoin (cartes de l'onglet "Statistiques et besoins")
interface SyntheseBesoinData {
  annee: number;
  organisme_id: number;
  perimetre: string | null;
  perimetre_detail?: PerimetreValue | null;
  montant_moyen: number;
  evolution_financiere: number | null;
  nombre_commandes: number;
  repartition: { MS: number; BC: number };
  utilisateurs: { actifs: number; total: number };
  // Annee en cours (montant_total) + valeurs de l'annee precedente (N-1) pour les cartes.
  montant_total?: number;
  // Total financier previsionnel de l'annee en cours (= total du tableau Estimation des besoins) :
  // denominateur de la carte Budget (consomme live / previsionnel).
  montant_total_previsionnel?: number;
  montant_total_precedent?: number;
  montant_moyen_precedent?: number;
  evolution_financiere_precedent?: number | null;
  nombre_commandes_precedent?: number;
  repartition_precedent?: { MS: number; BC: number };
  utilisateurs_precedent?: { actifs: number; total: number };
  sources: Record<string, boolean>;
}

const TABS = [
  "Présentation",
  "Droits",
  "Documents",
  "Coordonnées"
];

const DOCUMENT_TABS = [
  "Logo entreprise",
  "Pièces du marché",
  "Assurance",
  "Attestation fiscale et sociale",
  "Certifications et attestations"
];

type ContactKeys = 'nom' | 'prenom' | 'telephone' | 'fonction';

type OrganismeKeys = 'nom' | 'siret' | 'adresse' | 'ville' | 'codePostal';

interface IgnSuggestion {
  label: string;
  houseNumber?: string;
  street?: string;
  postcode?: string;
  city?: string;
}

interface Contact {
  nom: string;
  prenom: string;
  email: string;
  telephone: string;       // Téléphone de contact
  fonction: string; // Nouvelle propriété
  isEditing: boolean;
  role: string;
}

interface Organisme {
  nom: string;
  siret: string;
  adresse: string;
  ville: string;
  codePostal: string;
  numeroVoie: string;
  typeVoie: string;
  nomVoie: string;
  departement: string;
  isEditing: boolean;
  id?: string | number;
  pays: string;
  presentation_organisme: string;
  type_organisme: string;
  perimetre: string;
  representant_1_prenom: string;
  representant_1_nom: string;
  representant_1_fonction: string;
  representant_2_prenom: string;
  representant_2_nom: string;
  representant_2_fonction: string;
  representant_3_prenom: string;
  representant_3_nom: string;
  representant_3_fonction: string;
}

interface ProfileData {
  logo: string;
  entreprise: string;
  contact: Contact;
  organisme: Organisme;
}

const Profil: React.FC = () => {
  // Récupération des informations utilisateur depuis le contexte d'authentification
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { documents: acheteurDocuments, logoUrl: acheteurLogoUrl, setCompletion: setAcheteurCompletionCtx } = useAcheteurProfile();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [activeDocTab, setActiveDocTab] = useState<number | null>(null);
  // Cache de la synthese "Statistiques et besoins" (chargee une seule fois a
  // l'ouverture de l'onglet, cf. effet plus bas). Contrat de fraicheur du perimetre,
  // qui se modifie dans l'onglet Presentation mais s'affiche ici :
  //   1. au succes du PUT perimetre (saveOrganismePerimetre), on remet ce cache a
  //      null -> l'effet le rechargera a la prochaine ouverture de Stats ;
  //   2. handleTabChange flush la sauvegarde perimetre en attente (debounce) AVANT
  //      d'ouvrir Stats, pour couvrir le cas "je modifie puis je clique tout de suite".
  // Resultat : le perimetre affiche dans Stats suit toujours la derniere modif.
  const [syntheseBesoin, setSyntheseBesoin] = useState<SyntheseBesoinData | null>(null);
  const [syntheseBesoinLoading, setSyntheseBesoinLoading] = useState<boolean>(false);
  const [evaluationCentralis, setEvaluationCentralis] = useState<EvaluationCentralisData | null>(null);
  const [evaluationCentralisLoading, setEvaluationCentralisLoading] = useState<boolean>(false);
  // Avis deposes par cet acheteur sur les prestataires (depuis les commandes validees).
  const [avisDonnes, setAvisDonnes] = useState<AvisData[]>([]);
  const [avisDonnesLoaded, setAvisDonnesLoaded] = useState<boolean>(false);
  // Avis RECUS de la part des prestataires sur cette structure acheteur (PRIVE : 2 parties seules).
  const [avisAcheteurRecus, setAvisAcheteurRecus] = useState<AvisData[]>([]);
  const [avisAcheteurMoyenne, setAvisAcheteurMoyenne] = useState<number | null>(null);
  const [avisAcheteurRecusLoaded, setAvisAcheteurRecusLoaded] = useState<boolean>(false);
  // Derniere modification des besoins (remontee par EstimationBesoins ET LocalisationBesoins)
  // -> banniere "Derniere mise a jour".
  const [derniereMaj, setDerniereMaj] = useState<DerniereMaj | null>(null);
  // Les deux tableaux remontent leur propre derniere maj : on conserve la PLUS RECENTE et on
  // n'ecrase jamais une maj existante avec un null (sinon le 2e fetch resolu efface le 1er).
  const handleMajChange = useCallback((m: DerniereMaj | null) => {
    setDerniereMaj((cur) => {
      if (!m?.date) return cur;
      if (!cur?.date) return m;
      return new Date(m.date).getTime() > new Date(cur.date).getTime() ? m : cur;
    });
  }, []);

  // Declenche le telechargement d'un blob (lien temporaire).
  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  // Bouton 1 : CSV des 2 tableaux de la page (estimation + localisation).
  const handleExportStats = useCallback(async () => {
    try {
      const [est, loc, lots] = await Promise.all([
        apiService.getEstimationBesoins(),
        apiService.getLocalisationBesoins(),
        apiService.getLotsTechniques(),
      ]);
      const lotsTech = (Array.isArray(lots) ? lots : []).slice().sort((a: { code: string }, b: { code: string }) => Number(a.code) - Number(b.code));
      const csv = buildStatsBesoinsCsv(est, loc, lotsTech);
      triggerBlobDownload(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `statistiques-et-besoins-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch {
      alert('Une erreur est survenue lors du téléchargement des statistiques et besoins.');
    }
  }, []);

  // Bouton 2 : CSV de l'historique des commandes de l'organisme (meme export que la page Operations).
  const handleExportCommandes = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/operations/exportOperations`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Echec export');
      triggerBlobDownload(await response.blob(), `historique-commandes-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch {
      alert('Une erreur est survenue lors du téléchargement de l’historique des commandes.');
    }
  }, []);

  // Export PDF de tous les avis deposes par l'organisme (section "Avis concernant les prestataires").
  const handleExportAvisPdf = useCallback(async () => {
    try {
      const blob = await pdf(
        <AvisPDF
          avis={avisDonnes}
          cote="acheteur"
          generatedAt={new Date().toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' })}
          generatedBy={user?.name || user?.email || '—'}
        />,
      ).toBlob();
      triggerBlobDownload(blob, `avis-prestataires-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch {
      alert('Une erreur est survenue lors de la génération du PDF des avis.');
    }
  }, [avisDonnes, user]);

  // Export PDF des avis RECUS des prestataires (section privee "Avis des prestataires concernant
  // votre structure"). Meme generateur, sens "recus" (titres adaptes).
  const handleExportAvisAcheteurPdf = useCallback(async () => {
    try {
      const blob = await pdf(
        <AvisPDF
          avis={avisAcheteurRecus}
          cote="acheteur"
          sens="recus"
          generatedAt={new Date().toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' })}
          generatedBy={user?.name || user?.email || '—'}
        />,
      ).toBlob();
      triggerBlobDownload(blob, `avis-recus-prestataires-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch {
      alert('Une erreur est survenue lors de la génération du PDF des avis.');
    }
  }, [avisAcheteurRecus, user]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneConnexionError, setPhoneConnexionError] = useState<string | null>(null);
  const [siretError, setSiretError] = useState<string | null>(null);
  const [showModificationModal, setShowModificationModal] = useState<boolean>(false);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false); // Cache flag
  // Perimetre geographique structure (national / regions / departements).
  const [perimetreDetail, setPerimetreDetail] = useState<PerimetreValue>({ type: null, regions: [], departements: [] });
  // Signature du perimetre deja persiste par la section Statistiques (LocalisationBesoins).
  // Quand on synchronise perimetreDetail depuis cette section, l'autosave du selecteur de Presentation
  // se redeclencherait pour rien : ce ref lui dit de sauter ce PUT (deja ecrit).
  const externalPerimetreSyncRef = useRef<string | null>(null);

  // États pour l'autocomplétion d'adresse IGN
  const [adresseInput, setAdresseInput] = useState('');
  const [adresseSuggestions, setAdresseSuggestions] = useState<IgnSuggestion[]>([]);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const [modificationData, setModificationData] = useState({
    nom: '',
    prenom: '',
    fonction: '',
    telephone: '',
    email: ''
  });

  // Données du profil
  const [profileData, setProfileData] = useState<ProfileData>({
    logo: '',
    entreprise: "Nom de l'entreprise",
    contact: {
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      fonction: '',
      isEditing: false,
      role: user?.role || '' // Utiliser directement le rôle de l'utilisateur connecté
    },
    organisme: {
      nom: '',
      siret: '',
      adresse: '',
      ville: '',
      codePostal: '',
      numeroVoie: '',
      typeVoie: '',
      nomVoie: '',
      departement: '',
      isEditing: false,
      id: undefined,
      pays: 'France',
      presentation_organisme: '',
      type_organisme: '',
      perimetre: '',
      representant_1_prenom: '',
      representant_1_nom: '',
      representant_1_fonction: '',
      representant_2_prenom: '',
      representant_2_nom: '',
      representant_2_fonction: '',
      representant_3_prenom: '',
      representant_3_nom: '',
      representant_3_fonction: ''
    }
  });

  // Mémoïsation de la fonction getContactUpdateRoute
  const getContactUpdateRoute = React.useMemo(() => {
    return (role: string) => {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      switch (role) {
        case 'Acheteur signataire admin':
          return `${apiUrl}/responsables-achat/me`;
        case 'Acheteur signataire':
          // Le role UI "Acheteur signataire" est porte par un validateur_achat (cf UserInfo.tsx) :
          // /validateurs-achat/me renvoie l'organisme. /signataires/me (endpoint prestataire) ne le
          // renvoyait pas -> Presentation + Droits vides pour ce role. Corrige.
          return `${apiUrl}/validateurs-achat/me`;
        case 'Acheteur':
          return `${apiUrl}/acheteurs/me`;
        case 'Prestataire signataire admin':
          return `${apiUrl}/gestionnaires/me`;
        case 'Prestataire signataire':
          return `${apiUrl}/signataires/me`;
        case 'Prestataire':
          return `${apiUrl}/interlocuteurs/me`;
        // EPIC-29 Story 29.4 — Prestataires libres
        case 'Prestataire_Libre_Admin':
        case 'Prestataire_Libre':
          return `${apiUrl}/prestataires-libres/me`;
        default:
          return '';
      }
    };
  }, []);

  // Nos propres implémentations de isClientRole et isPrestataireRole (déplacées avant useEffect)
  const isClientRoleLocal = React.useCallback((role: string | undefined): boolean => {
    if (!role) return false;
    return ['Acheteur', 'Acheteur signataire', 'Acheteur signataire admin'].includes(role);
  }, []);

  const isPrestataireRoleLocal = React.useCallback((role: string | undefined): boolean => {
    if (!role) return false;
    return ['Prestataire', 'Prestataire signataire', 'Prestataire signataire admin'].includes(role);
  }, []);

  // Charger les données de l'utilisateur depuis l'API - OPTIMISÉ
  useEffect(() => {
    // Ne charger qu'une seule fois
    if (dataLoaded) return;

    const fetchUserData = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Aucun token d\'authentification trouvé');

        const apiUrl = process.env.REACT_APP_API_URL || '';
        const userRoute = getContactUpdateRoute(user?.role || '');

        // OPTIMISATION : Paralléliser les appels API au lieu de les faire séquentiellement
        const requests: Promise<Response>[] = [
          // 1. Récupérer les infos de connexion via /me
          fetch(`${apiUrl}/me`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          })
        ];

        // 2. Ajouter la requête utilisateur métier si la route existe
        if (userRoute) {
          requests.push(
            fetch(userRoute, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              }
            })
          );
        }

        // Exécuter toutes les requêtes en parallèle
        const responses = await Promise.all(requests);

        if (!responses[0].ok) throw new Error('Erreur lors de la récupération des informations de connexion');

        const [meDataRaw, userDataRaw] = await Promise.all([
          responses[0].json(),
          responses[1]?.ok ? responses[1].json() : Promise.resolve({ data: {} })
        ]);

        const meData = meDataRaw.data;
        const userData = userDataRaw.data || {};

        // Pour les prestataires, c'est id_entite, pour les acheteurs, c'est id_organisme
        const idOrganisme = userData?.id_organisme || userData?.id_entite;

        let organismeData = {
          nom: '',
          siret: '',
          adresse: '',
          ville: '',
          codePostal: '',
          numeroVoie: '',
          typeVoie: '',
          nomVoie: '',
          departement: '',
          isEditing: false,
          id: undefined as string | number | undefined,
          pays: 'France',
          presentation_organisme: '',
          type_organisme: '',
          perimetre: '',
          representant_1_prenom: '',
          representant_1_nom: '',
          representant_1_fonction: '',
          representant_2_prenom: '',
          representant_2_nom: '',
          representant_2_fonction: '',
          representant_3_prenom: '',
          representant_3_nom: '',
          representant_3_fonction: ''
        };

        // Récupérer nom, siret et id selon le rôle
        if (isPrestataireRoleLocal(user?.role)) {
          if (userData.entite) {
            organismeData.nom = userData.entite.nom || '';
            organismeData.id = userData.entite.id;
            if (userData.entite.entreprises && userData.entite.entreprises.length > 0) {
              organismeData.siret = String(userData.entite.entreprises[0].siret) || '';
            }
          }
        } else {
          if (userData.organisme) {
            organismeData.nom = userData.organisme.nom || '';
            organismeData.siret = userData.organisme.siret || '';
            organismeData.id = userData.organisme.id;
          }
        }

        // 3. Récupérer adresse uniquement si nécessaire
        if (idOrganisme) {
          const routeType = isPrestataireRoleLocal(user?.role) ? 'entites' : 'organismes';
          const orgResponse = await fetch(`${apiUrl}/${routeType}/${idOrganisme}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (orgResponse.ok) {
            const orgData = await orgResponse.json();
            if (orgData.status === 'success' && orgData.data) {
              const adresseObj = orgData.data.adresse;

              // Nettoyer nom_voie si elle commence par le numéro de voie
              let nomVoieClean = adresseObj?.nom_voie || '';
              const numeroVoie = adresseObj?.numero_voie || '';
              if (numeroVoie && nomVoieClean.startsWith(numeroVoie + ' ')) {
                nomVoieClean = nomVoieClean.substring((numeroVoie + ' ').length);
              }

              // `numero_voie` vaut « 0 » sur pres d'un millier d'adresses en base : c'est une sentinelle
              // « pas de numero », pas le numero zero (cf. utils/adresse).
              organismeData.adresse = adresseObj
                ? `${ligneVoie(adresseObj.numero_voie, adresseObj.type_voie, nomVoieClean)}${adresseObj.arrondissement ? ', ' + adresseObj.arrondissement : ''}`
                : '';
              organismeData.ville = adresseObj?.ville || '';
              organismeData.codePostal = adresseObj?.code_postal || '';
              organismeData.numeroVoie = numeroVoie;
              organismeData.typeVoie = adresseObj?.type_voie || '';
              organismeData.nomVoie = nomVoieClean;
              organismeData.departement = adresseObj?.departement || '';

              // Ligne complete (voie, code postal ville, pays) : c'est le seul champ adresse affiche,
              // il doit donc porter toute la localisation. Les separateurs viennent de l'utilitaire
              // partage pour que les quatre surfaces adresse de l'application se lisent pareil.
              const adresseComplete = adresseUneLigne(
                ligneVoie(numeroVoie, adresseObj?.type_voie, nomVoieClean),
                adresseObj?.code_postal,
                adresseObj?.ville,
                adresseObj?.pays,
              );
              setAdresseInput(adresseComplete);

              // Charger pays, présentation et type organisme
              organismeData.pays = adresseObj?.pays || 'France';
              organismeData.presentation_organisme = orgData.data.presentation || '';
              organismeData.type_organisme = orgData.data.type_organisme || '';
              organismeData.perimetre = orgData.data.perimetre_effectif || orgData.data.perimetre || '';
              // Perimetre structure (selecteur) : pre-remplir depuis le detail renvoye par l'API.
              const pd = orgData.data.perimetre_detail;
              if (pd) {
                setPerimetreDetail({
                  type: pd.type ?? null,
                  regions: Array.isArray(pd.regions) ? pd.regions : [],
                  departements: Array.isArray(pd.departements) ? pd.departements : [],
                });
              }
              // Représentants légaux
              organismeData.representant_1_prenom = orgData.data.representant_1_prenom || '';
              organismeData.representant_1_nom = orgData.data.representant_1_nom || '';
              organismeData.representant_1_fonction = orgData.data.representant_1_fonction || '';
              organismeData.representant_2_prenom = orgData.data.representant_2_prenom || '';
              organismeData.representant_2_nom = orgData.data.representant_2_nom || '';
              organismeData.representant_2_fonction = orgData.data.representant_2_fonction || '';
              organismeData.representant_3_prenom = orgData.data.representant_3_prenom || '';
              organismeData.representant_3_nom = orgData.data.representant_3_nom || '';
              organismeData.representant_3_fonction = orgData.data.representant_3_fonction || '';
            }
          }
        }

        setProfileData(prev => ({
          ...prev,
          contact: {
            ...prev.contact,
            nom: userData?.nom || prev.contact.nom,
            prenom: userData?.prenom || prev.contact.prenom,
            email: meData.email || '',
            role: user?.role || '',
            telephone: userData?.telephone || prev.contact.telephone,
            fonction: userData?.fonction || prev.contact.fonction
          },
          organisme: organismeData
        }));

        setDataLoaded(true); // Marquer les données comme chargées

      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur:', error);
        setError('Impossible de récupérer les informations utilisateur.');
      } finally {
        setLoading(false);
      }
    };

    if (user?.role) {
      fetchUserData();
    }
  }, [user?.role, dataLoaded, getContactUpdateRoute, isClientRoleLocal, isPrestataireRoleLocal]);

  // Effet pour l'autocomplétion d'adresse IGN
  useEffect(() => {
    if (adresseInput.trim().length < 3) {
      setAdresseSuggestions([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const controller = new AbortController();

      fetch(`https://data.geopf.fr/geocodage/completion?text=${encodeURIComponent(adresseInput)}&index=StreetAddress&limit=8`,
        { signal: controller.signal })
        .then(r => r.ok ? r.json() : Promise.reject(r))
        .then(json => {
          console.log('IGN completion', json);
          if (json && json.results) {
            // Transformation des résultats dans le format IgnSuggestion
            const suggestions = json.results.map((result: any) => ({
              label: result.fulltext,
              houseNumber: result.kind === 'housenumber' ? result.fulltext.split(' ')[0] : '',
              street: result.street,
              postcode: result.zipcode,
              city: result.city
            }));
            console.log('Suggestions transformées:', suggestions);
            setAdresseSuggestions(suggestions);
          }
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error('Erreur lors de la récupération des adresses:', error);
          }
        });

      return () => controller.abort();
    }, 300);
  }, [adresseInput]);

  // Fonction utilitaire pour détecter le type de voie
  const detectTypeVoie = (street?: string): string => {
    if (!street) return '';
    const s = street.toLowerCase();
    if (s.startsWith('rue')) return 'rue';
    if (s.startsWith('avenue')) return 'avenue';
    if (s.startsWith('boulevard')) return 'boulevard';
    if (s.startsWith('impasse')) return 'impasse';
    if (s.startsWith('allée')) return 'allée';
    if (s.startsWith('place')) return 'place';
    return '';
  };

  // Sélection d'une adresse depuis les suggestions IGN
  const handleSelectAdresse = (suggestion: IgnSuggestion) => {
    const type = detectTypeVoie(suggestion.street);
    let nomVoie = suggestion.street || '';

    // Retirer le type de voie du nom de la voie
    if (type && nomVoie) {
      const typeRegex = new RegExp(`^${type}\\s+`, 'i');
      nomVoie = nomVoie.replace(typeRegex, '');
    }

    setProfileData(prev => ({
      ...prev,
      organisme: {
        ...prev.organisme,
        numeroVoie: suggestion.houseNumber || '',
        nomVoie: nomVoie,
        codePostal: suggestion.postcode || '',
        ville: suggestion.city || '',
        departement: departementFromCodePostal(suggestion.postcode),
        typeVoie: type,
        adresse: `${suggestion.houseNumber || ''} ${type} ${nomVoie}`.trim()
      }
    }));

    // `fulltext` IGN vaut deja « voie, code postal ville » ; seul le pays manque. On l'ajoute plutot
    // que de recomposer depuis les morceaux : quand IGN ne renvoie pas `street`, la voie reconstruite
    // est vide et l'utilisateur perdrait l'adresse qu'il vient de choisir.
    setAdresseInput(adresseUneLigne(suggestion.label, '', '', 'France'));
    setAdresseSuggestions([]);
  };

  // Navigation rapide retour
  const handleBackToProfil = () => {
    setActiveDocTab(null);
    setActiveTab(0);
  };

  // Fonction pour vérifier si l'utilisateur est autorisé à modifier ses informations
  const canEditInfo = () => {
    const role = user?.role || '';
    // Interlocuteur et Acheteur ne peuvent pas modifier les infos
    if (role === 'Prestataire' || role === 'Acheteur') {
      return false;
    }
    // Les administrateurs (gestionnaires) ne peuvent pas modifier leurs coordonnées
    if (role === 'Acheteur signataire admin' || role === 'Prestataire signataire admin') {
      return false;
    }
    return true;
  };

  // SIMPLE: Acheteur signataire admin peut modifier le bloc Présentation
  const canEditOrganisme = () => {
    return user?.role === 'Acheteur signataire admin';
  };

  // Fonction spécifique pour l'édition du SIRET — EPIC-29 Story 29.2
  // Acheteur signataire admin + Prestataire_Libre_Admin peuvent modifier le SIRET
  const canEditSiret = () => {
    return user?.role === 'Acheteur signataire admin' || user?.role === 'Prestataire_Libre_Admin';
  };

  // Fonction pour déterminer le type d'action pour le bouton de modification du profil
  const getProfileActionType = () => {
    const role = user?.role || '';

    if (role === 'Acheteur signataire admin' || role === 'Prestataire signataire admin') {
      return 'Modifier'; // Les admins peuvent faire des passations
    } else if (role === 'Acheteur' || role === 'Prestataire') {
      return 'demande'; // Les simples utilisateurs font des demandes
    } else if (['Acheteur signataire', 'Prestataire signataire'].includes(role)) {
      return 'Modifier'; // Les signataires peuvent aussi faire des passations
    } else {
      return 'demande'; // Par défaut
    }
  };

  // Validation du téléphone - exige un mobile français (06 ou 07, 10 chiffres)
  const validatePhoneNumber = (phoneNumber: string, role: string = user?.role || '') => {
    const cleanNumber = phoneNumber.replace(/\s+/g, '').replace(/[^0-9]/g, '');
    // Mobile français uniquement : commence par 06 ou 07, 10 chiffres
    return /^0[67]\d{8}$/.test(cleanNumber);
  };

  // Ajouter en haut avec les autres fonctions
  const validateSiret = (siret: string) => {
    // Un SIRET valide contient 14 chiffres
    return /^\d{14}$/.test(siret.replace(/\s/g, ''));
  };

  // Fonction d'initialisation des données de modification
  const initModificationData = () => {
    setModificationData({
      nom: profileData.contact.nom,
      prenom: profileData.contact.prenom,
      fonction: profileData.contact.fonction,
      telephone: profileData.contact.telephone,
      email: profileData.contact.email
    });
  };

  // Fonction d'initialisation des données de modification
  const handleModificationChange = (field: string, value: string) => {
    setModificationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fonction pour gérer l'envoi des modifications (fonction existante)
  const handleSubmitModification = async () => {
    // ...
  };

  // Fonction pour mettre à jour les informations de contact (Acheteur signataire admin)
  const handleUpdateContactInfo = async () => {
    if (!user?.role) return;

    // Validation du téléphone - obligatoire et mobile français (06/07)
    if (!profileData.contact.telephone.trim() || !validatePhoneNumber(profileData.contact.telephone, user.role)) {
      setPhoneError("Le numero doit etre un mobile francais valide (06 ou 07, 10 chiffres)");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token d\'authentification non trouvé');

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const updateRoute = getContactUpdateRoute(user.role);

      const response = await fetch(updateRoute, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          nom: profileData.contact.nom,
          prenom: profileData.contact.prenom,
          telephone: profileData.contact.telephone,
          fonction: profileData.contact.fonction
          // Note: email n'est pas inclus car sa modification est en développement
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour des informations');
      }

      const data = await response.json();
      if (data.status === 'success') {
        alert('Informations mises à jour avec succès');
        // Recharger les données
        setDataLoaded(false);
      } else {
        throw new Error(data.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
    }
  };

  // EPIC-29 Story 29.2 — Mise à jour SIRET via PUT /profile/siret (Prestataire_Libre_Admin)
  const handleSiretUpdate = async () => {
    if (!canEditSiret() || !profileData.organisme.siret) return;

    if (!validateSiret(profileData.organisme.siret)) {
      setSiretError('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token d\'authentification non trouvé');

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/profile/siret`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ siret: profileData.organisme.siret }),
      });

      const data = await response.json();

      if (!response.ok || data.status !== 'success') {
        throw new Error(data.message || 'Erreur lors de la mise à jour du SIRET');
      }

      alert('SIRET mis à jour avec succès.');
      setSiretError(null);
      setDataLoaded(false);
    } catch (error) {
      console.error('Erreur SIRET:', error);
      setSiretError(error instanceof Error ? error.message : 'Une erreur est survenue');
    }
  };

  // Utilitaire interne : envoie un subset de champs via PUT /organismes/:id
  const putOrganisme = useCallback(async (id: string | number, fields: Record<string, unknown>) => {
    if (user?.role !== 'Acheteur signataire admin') return;
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const response = await fetch(`${apiUrl}/organismes/${id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const err: any = new Error(errorData.message || 'Erreur lors de la mise à jour');
      err.status = response.status;
      err.errors = errorData.errors ?? null;
      throw err;
    }
  }, [user?.role]);

  // Autosave indépendant — SIRET (Acheteur signataire admin uniquement)
  const saveOrganismeSiret = useCallback(async (data: { id?: string | number; siret: string }) => {
    if (!data.id) return;
    const siretClean = data.siret.replace(/[^0-9]/g, '');
    if (!/^\d{14}$/.test(siretClean)) return; // Ne pas sauvegarder un SIRET invalide
    await putOrganisme(data.id, { siret: siretClean });
  }, [putOrganisme]);

  const autoSaveSiret = useAutoSave({
    data: { id: profileData.organisme.id, siret: profileData.organisme.siret },
    saveFn: saveOrganismeSiret,
    delay: 1500,
    enabled: dataLoaded && user?.role === 'Acheteur signataire admin',
  });

  // Autosave indépendant — Nom
  const saveOrganismeNom = useCallback(async (data: { id?: string | number; nom: string }) => {
    if (!data.id) return;
    await putOrganisme(data.id, { nom: data.nom });
  }, [putOrganisme]);

  // Autosave indépendant — Adresse
  const saveOrganismeAdresse = useCallback(async (data: {
    id?: string | number;
    adresse: string; numeroVoie: string; typeVoie: string; nomVoie: string;
    codePostal: string; ville: string; pays: string; departement: string;
  }) => {
    if (!data.id) return;
    await putOrganisme(data.id, {
      adresse: data.adresse,
      numero_voie: data.numeroVoie,
      type_voie: data.typeVoie,
      nom_voie: data.nomVoie,
      code_postal: data.codePostal,
      departement: data.departement,
      ville: data.ville,
      pays: data.pays,
    });
  }, [putOrganisme]);

  // Autosave indépendant — Présentation
  const saveOrganismePresentation = useCallback(async (data: {
    id?: string | number;
    type_organisme: string;
    presentation_organisme: string;
  }) => {
    if (!data.id) return;
    await putOrganisme(data.id, {
      type_organisme: data.type_organisme || null,
      presentation: data.presentation_organisme,
    });
  }, [putOrganisme]);

  const autoSaveNom = useAutoSave({
    data: { id: profileData.organisme.id, nom: profileData.organisme.nom },
    saveFn: saveOrganismeNom,
    delay: 1500,
    enabled: dataLoaded && user?.role === 'Acheteur signataire admin',
  });

  const autoSaveAdresse = useAutoSave({
    data: {
      id: profileData.organisme.id,
      adresse: profileData.organisme.adresse,
      numeroVoie: profileData.organisme.numeroVoie,
      typeVoie: profileData.organisme.typeVoie,
      nomVoie: profileData.organisme.nomVoie,
      codePostal: profileData.organisme.codePostal,
      ville: profileData.organisme.ville,
      pays: profileData.organisme.pays,
      departement: profileData.organisme.departement,
    },
    saveFn: saveOrganismeAdresse,
    delay: 1500,
    enabled: dataLoaded && user?.role === 'Acheteur signataire admin',
  });

  const autoSavePresentation = useAutoSave({
    data: {
      id: profileData.organisme.id,
      type_organisme: profileData.organisme.type_organisme,
      presentation_organisme: profileData.organisme.presentation_organisme,
    },
    saveFn: saveOrganismePresentation,
    delay: 1500,
    enabled: dataLoaded && user?.role === 'Acheteur signataire admin',
  });

  // Autosave indépendant — Périmètre structuré de l'organisme (national / zones).
  const saveOrganismePerimetre = useCallback(async (data: {
    id?: string | number;
    type: 'national' | 'zones' | null;
    region_ids: number[];
    departement_ids: number[];
  }) => {
    if (!data.id || !data.type) return; // type null = perimetre pas encore defini par l'utilisateur
    if (user?.role !== 'Acheteur signataire admin') return;
    // Deja persiste par la section Statistiques (LocalisationBesoins) : on saute le PUT redondant.
    // Ref consomme en one-shot (toujours remis a null), pour ne jamais sauter un futur vrai save.
    const signature = JSON.stringify({ type: data.type, region_ids: data.region_ids, departement_ids: data.departement_ids });
    const skipSignature = externalPerimetreSyncRef.current;
    externalPerimetreSyncRef.current = null;
    if (skipSignature === signature) {
      setSyntheseBesoin(null);
      return;
    }
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const response = await fetch(`${apiUrl}/organismes/${data.id}/perimetre`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        perimetre_type: data.type,
        region_ids: data.region_ids,
        departement_ids: data.departement_ids,
      }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const err: any = new Error(errorData.message || 'Erreur lors de la mise à jour du périmètre');
      err.status = response.status;
      err.errors = errorData.errors ?? null;
      throw err;
    }
    // Le perimetre a change : invalider le cache de la synthese Stats pour qu'elle
    // se recharge (perimetre a jour) a la prochaine ouverture de l'onglet.
    setSyntheseBesoin(null);
  }, [user?.role]);

  const autoSavePerimetre = useAutoSave({
    data: {
      id: profileData.organisme.id,
      type: perimetreDetail.type,
      region_ids: perimetreDetail.regions.map(r => r.id),
      departement_ids: perimetreDetail.departements.map(d => d.id),
    },
    saveFn: saveOrganismePerimetre,
    delay: 1500,
    enabled: dataLoaded && user?.role === 'Acheteur signataire admin',
  });

  // Perimetre modifie depuis la section Statistiques (deja persiste la-bas) : on synchronise le
  // selecteur de l'onglet Presentation tout de suite + on invalide la synthese, sans re-sauver.
  const handlePerimetreFromStats = useCallback((value: PerimetreValue) => {
    externalPerimetreSyncRef.current = JSON.stringify({
      type: value.type,
      region_ids: value.regions.map(r => r.id),
      departement_ids: value.departements.map(d => d.id),
    });
    setPerimetreDetail(value);
    setSyntheseBesoin(null);
  }, []);

  // Autosave indépendant — Représentants légaux
  const saveOrganismeRepresentants = useCallback(async (data: {
    id?: string | number;
    representant_1_prenom: string; representant_1_nom: string; representant_1_fonction: string;
    representant_2_prenom: string; representant_2_nom: string; representant_2_fonction: string;
    representant_3_prenom: string; representant_3_nom: string; representant_3_fonction: string;
  }) => {
    if (!data.id) return;
    await putOrganisme(data.id, {
      representant_1_prenom: data.representant_1_prenom || null,
      representant_1_nom: data.representant_1_nom || null,
      representant_1_fonction: data.representant_1_fonction || null,
      representant_2_prenom: data.representant_2_prenom || null,
      representant_2_nom: data.representant_2_nom || null,
      representant_2_fonction: data.representant_2_fonction || null,
      representant_3_prenom: data.representant_3_prenom || null,
      representant_3_nom: data.representant_3_nom || null,
      representant_3_fonction: data.representant_3_fonction || null,
    });
  }, [putOrganisme]);

  const autoSaveRepresentants = useAutoSave({
    data: {
      id: profileData.organisme.id,
      representant_1_prenom: profileData.organisme.representant_1_prenom,
      representant_1_nom: profileData.organisme.representant_1_nom,
      representant_1_fonction: profileData.organisme.representant_1_fonction,
      representant_2_prenom: profileData.organisme.representant_2_prenom,
      representant_2_nom: profileData.organisme.representant_2_nom,
      representant_2_fonction: profileData.organisme.representant_2_fonction,
      representant_3_prenom: profileData.organisme.representant_3_prenom,
      representant_3_nom: profileData.organisme.representant_3_nom,
      representant_3_fonction: profileData.organisme.representant_3_fonction,
    },
    saveFn: saveOrganismeRepresentants,
    delay: 1500,
    enabled: dataLoaded && user?.role === 'Acheteur signataire admin',
  });

  // Statut combiné pour le toast (priorité : erreur > saving > pending > saved)
  const allStatuses = [autoSaveSiret.status, autoSaveNom.status, autoSaveAdresse.status, autoSavePresentation.status, autoSavePerimetre.status, autoSaveRepresentants.status];
  const combinedAutoSaveStatus =
    allStatuses.find(s => s === 'error') ??
    allStatuses.find(s => s === 'saving') ??
    allStatuses.find(s => s === 'pending') ??
    allStatuses.find(s => s === 'saved') ??
    'idle';
  const combinedAutoSaveError = autoSaveSiret.error || autoSaveNom.error || autoSaveAdresse.error || autoSavePresentation.error || autoSavePerimetre.error || autoSaveRepresentants.error;

  // ---- Complétion profil acheteur (présentation + documents) ----
  const acheteurCompletion = React.useMemo(() => {
    if (!isClientRoleLocal(user?.role) || !dataLoaded) return null;
    const org = profileData.organisme;
    const presentationComplete = Boolean(
      org.nom?.trim() &&
      org.siret?.replace(/\D/g, '').length === 14 &&
      (org.nomVoie?.trim() || org.adresse?.trim()) &&
      org.codePostal?.trim() &&
      org.ville?.trim() &&
      org.type_organisme?.trim() &&
      org.presentation_organisme?.trim() &&
      org.representant_1_prenom?.trim() &&
      org.representant_1_nom?.trim() &&
      org.representant_1_fonction?.trim()
    );
    const logoComplete = Boolean(acheteurLogoUrl);
    // Seule l'habilitation administrateur est obligatoire ; la délibération reste facultative.
    const documentsComplete = Boolean(acheteurDocuments.habilitation_admin);
    // Onglet Documents (logo + habilitation) compté en tout ou rien : 50% accordés seulement
    // quand tous ses éléments obligatoires sont présents.
    const documentsTabComplete = logoComplete && documentsComplete;
    const percent = (presentationComplete ? 50 : 0)
      + (documentsTabComplete ? 50 : 0);
    return {
      percent,
      sections: { presentation: presentationComplete, logo: logoComplete, documents: documentsComplete },
      is_complete: percent === 100,
    };
  }, [profileData.organisme, acheteurDocuments, acheteurLogoUrl, user?.role, isClientRoleLocal, dataLoaded]);

  // Source de verite unique pour la pastille sidebar : on pousse le calcul local (instantane)
  // dans le contexte que lit la SideBar, au lieu de dependre d'un refetch backend desynchronise.
  useEffect(() => {
    if (acheteurCompletion) setAcheteurCompletionCtx(acheteurCompletion);
  }, [acheteurCompletion, setAcheteurCompletionCtx]);

  // Toast AlertMessage (même pattern que ProfilUnifie)
  const [organismeToast, setOrganismeToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const prevAutoSaveStatusRef = useRef(combinedAutoSaveStatus);

  useEffect(() => {
    const prev = prevAutoSaveStatusRef.current;
    prevAutoSaveStatusRef.current = combinedAutoSaveStatus;
    if (prev === 'saving' && combinedAutoSaveStatus === 'saved') {
      setOrganismeToast({ type: 'success', message: 'Informations organisme mises à jour avec succès' });
      // La pastille sidebar suit le calcul local pousse dans le contexte (cf useEffect plus haut) ;
      // pas de refetch backend ici, il pourrait ecraser la valeur locale avec une valeur desynchronisee.
    } else if (combinedAutoSaveStatus === 'error' && prev !== 'error') {
      setOrganismeToast({ type: 'error', message: combinedAutoSaveError || 'Erreur lors de la mise à jour' });
    }
  }, [combinedAutoSaveStatus, combinedAutoSaveError]);

  // Onglet coordonnées
  const renderCoordonnees = () => (
    <>
      <section className="profil-tab-page">
        {loading ? (
          <p className="loading-indicator">Chargement des informations...</p>
        ) : error ? (
          <p className="error-message">{error}</p>
        ) : (
          <>
            {/* Première section: Mes informations de contact */}
            {/* <h2 className='section-header'>Mes informations de contact</h2>
            <div className="section-content">
              <div className="form-group identifiant-connexion">
                <label>Identifiant de connexion</label>
                <div className="identifiant-connexion-input" title="Fonctionnalité en cours de développement">
                  <input
                    type="email"
                    value={profileData.contact.email}
                    className="input-inactif"
                  />
                  <Button
                    value="Modifier"
                    variant="primary"
                    size="small"
                    disabled={true}
                    onClick={() => { }}
                  />
                </div>
              </div>
              <br />
              <hr />
              <br />
              <div className="form-row grid-4">
                <div className="form-group">
                  <label>Nom</label>
                  <input
                    type="text"
                    value={profileData.contact.nom}
                    onChange={e => setProfileData(prev => ({
                      ...prev,
                      contact: { ...prev.contact, nom: e.target.value }
                    }))}
                    disabled={!canEditInfo()}
                    className={!canEditInfo() ? "input-inactif" : ""}
                  />
                </div>

                <div className="form-group">
                  <label>Prénom</label>
                  <input
                    type="text"
                    value={profileData.contact.prenom}
                    onChange={e => setProfileData(prev => ({
                      ...prev,
                      contact: { ...prev.contact, prenom: e.target.value }
                    }))}
                    disabled={!canEditInfo()}
                    className={!canEditInfo() ? "input-inactif" : ""}
                  />
                </div>
                <div className="form-group span-2">
                  <label>Fonction</label>
                  <input
                    type="text"
                    value={profileData.contact.fonction}
                    onChange={e => setProfileData(prev => ({
                      ...prev,
                      contact: { ...prev.contact, fonction: e.target.value }
                    }))}
                    disabled={!canEditInfo()}
                    className={!canEditInfo() ? "input-inactif" : ""}
                  />
                </div>

                <div className="form-group span-2">
                  <label>{isClientRoleLocal(user?.role) ? "Numéro de téléphone acheteur" : "Téléphone"}</label>
                  <input
                    type="text"
                    value={profileData.contact.telephone}
                    onChange={e => {
                      const newPhone = e.target.value;
                      setProfileData(prev => ({
                        ...prev,
                        contact: { ...prev.contact, telephone: newPhone }
                      }));
                      if (newPhone.trim()) {
                        if (!validatePhoneNumber(newPhone, user?.role || '')) {
                          setPhoneError("Le numero doit etre un mobile francais valide (06 ou 07, 10 chiffres)");
                        } else {
                          setPhoneError(null);
                        }
                      } else {
                        setPhoneError(null);
                      }
                    }}
                    disabled={!canEditInfo()}
                    className={phoneError ? "input-error" : (!canEditInfo() ? "input-inactif" : "")}
                  />
                  {phoneError && <p className="error-message">{phoneError}</p>}
                </div>

                <div className="form-group span-2">
                  <label>Email</label>
                  <input
                    type="email"
                    value={profileData.contact.email}
                    disabled={true}
                    className="input-inactif"
                    title="La modification de l'email est en cours de développement"
                  />
                </div>
              </div> */}

            {/* Message d'information selon le rôle */}
            {/*{user?.role === 'Acheteur signataire admin' || user?.role === 'Prestataire signataire admin' ? (
                <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                  <p className="info-text" style={{ marginBottom: '0.5rem' }}>
                    Note : La modification de l'email (identifiant de connexion) est en cours de développement.
                  </p>
                  <p style={{ fontSize: '0.9rem', color: '#f97316', backgroundColor: '#fff3e0', padding: '0.75rem', borderRadius: '4px', border: '1px solid #ffb74d' }}>
                    <strong>Restriction administrateur :</strong> En tant que gestionnaire, vous ne pouvez pas modifier vos coordonnées (nom, prénom, fonction, téléphone). Pour toute modification, veuillez contacter le support ou un autre gestionnaire.
                  </p>
                </div>
              ) : canEditInfo() ? (
                <p className="info-text" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                  Note : La modification de l'email (identifiant de connexion) est en cours de développement.
                </p>
              ) : (
                <p style={{ marginTop: '1rem', marginBottom: '1rem', fontSize: '0.9rem', color: '#666' }}>
                  Pour toute modification de vos informations, veuillez contacter votre administrateur.
                </p>
              )} */}

            {/* Bouton de mise à jour pour les informations de contact */}
            {/* {user?.role === 'Acheteur signataire admin' || user?.role === 'Prestataire signataire admin' ? (
                <div className="button-container">
                  <Button
                    value="Modification restreinte"
                    variant="secondary"
                    size="small"
                    disabled={true}
                    onClick={() => { }}
                  />
                </div>
              ) : canEditInfo() ? (
                <div className="button-container">
                  <Button
                    value="Modifier"
                    variant="primary"
                    size="small"
                    disabled={false}
                    onClick={handleUpdateContactInfo}
                  />
                </div>
              ) : (
                <div className="button-container">
                  <Button
                    value="Faire une demande"
                    variant="primary"
                    size="small"
                    disabled={true}
                    onClick={() => { }}
                  />
                </div>
              )}
            </div> */}
            {/* Deuxième section: Mes informations de connexion */}
            <section className="profil-tab-page">

              {/* Bouton pour envoyer une demande de modification - visible pour tous */}

            </section>

            {/* Section Coordonnées d'entreprise reste inchangée */}
            <SectionPliable
              title="Présentation"
              headerRight={user?.role === 'Acheteur signataire admin' && (
                <AutoSaveIndicator
                  status={combinedAutoSaveStatus}
                  error={combinedAutoSaveError}
                />
              )}
            >
                <div className="form-row grid-2">
                  {/* Nom de l'organisme sur une colonne */}
                  <div className="form-group">
                    <label>Nom de l'organisme<span className="required-asterisk">*</span></label>
                    <input
                      type="text"
                      value={profileData.organisme?.nom || ''} // Ajout du "?" et valeur par défaut
                      onChange={e => setProfileData(prev => ({
                        ...prev,
                        organisme: { ...(prev.organisme || {}), nom: e.target.value }
                      }))}
                      disabled={!canEditOrganisme()}
                      className={!canEditOrganisme() ? "input-inactif" : ""}
                    />
                  </div>

                  {/* SIRET sur une colonne - éditable uniquement pour Acheteur signataire admin */}
                  <div className="form-group">
                    <label>SIRET<span className="required-asterisk">*</span></label>
                    <input
                      type="text"
                      value={profileData.organisme.siret}
                      onChange={e => {
                        const value = e.target.value;
                        setProfileData(prev => ({
                          ...prev,
                          organisme: { ...prev.organisme, siret: value }
                        }));
                        if (value && !validateSiret(value)) {
                          setSiretError('Le SIRET doit contenir exactement 14 chiffres');
                        } else {
                          setSiretError(null);
                        }
                      }}
                      disabled={!canEditSiret()}
                      className={!canEditSiret() ? "input-inactif" : ""}
                      placeholder="14 chiffres"
                      maxLength={14}
                    />
                    <span className="char-count">{profileData.organisme.siret.replace(/[^0-9]/g, '').length}/14</span>
                    {siretError && <div className="error-message">{siretError}</div>}
                  </div>

                  {/* Bouton SIRET pour Prestataire_Libre_Admin */}
                  {user?.role === 'Prestataire_Libre_Admin' && canEditSiret() && (
                    <div className="form-group span-2">
                      <Button
                        value="Enregistrer le SIRET"
                        variant="primary"
                        size="small"
                        disabled={!!siretError || !profileData.organisme.siret}
                        onClick={handleSiretUpdate}
                      />
                    </div>
                  )}

                  {/* Adresse sur deux colonnes avec autocomplétion IGN */}
                  <div className="form-group" style={{ position: 'relative', gridColumn: '1 / -1' }}>
                    <label>Adresse<span className="required-asterisk">*</span></label>
                    <input
                      type="text"
                      value={adresseInput}
                      onChange={e => {
                        const newValue = e.target.value;
                        setAdresseInput(newValue);
                        // Taper INVALIDE la derivation : code postal, ville et pays ne sont plus
                        // affiches, ils ne peuvent venir que d'une suggestion IGN. Les vider est ce qui
                        // rend la regle verifiable — sinon une adresse retapee garderait la
                        // localisation de la precedente.
                        setProfileData(prev => ({
                          ...prev,
                          organisme: { ...prev.organisme, adresse: newValue, codePostal: '', ville: '', pays: '' }
                        }));
                      }}
                      onFocus={() => {
                        if (canEditOrganisme()) {
                          setProfileData(prev => ({
                            ...prev,
                            organisme: { ...prev.organisme, isEditing: true }
                          }));
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          setProfileData(prev => ({
                            ...prev,
                            organisme: { ...prev.organisme, isEditing: false }
                          }));
                          setAdresseSuggestions([]);
                        }, 200);
                      }}
                      disabled={!canEditOrganisme()}
                      className={!canEditOrganisme() ? "input-inactif" : ""}
                      placeholder="Commencez à taper une adresse..."
                      autoComplete="off"
                    />
                    {adresseSuggestions.length > 0 && profileData.organisme.isEditing && (
                      <ul className="autocomplete-suggestions" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        listStyle: 'none',
                        padding: 0,
                        margin: '4px 0 0 0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}>
                        {adresseSuggestions.map((suggestion, index) => (
                          <li
                            key={index}
                            onClick={() => handleSelectAdresse(suggestion)}
                            style={{
                              padding: '10px 15px',
                              cursor: 'pointer',
                              borderBottom: index < adresseSuggestions.length - 1 ? '1px solid #eee' : 'none'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                          >
                            {suggestion.label}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                </div>
                {/* Code postal, ville et pays : DERIVES de la suggestion IGN choisie, plus affiches.
                    Ils restent dans `profileData.organisme` et partent en base comme avant — seul le
                    rendu change, l'adresse tenant sur une seule ligne. Une saisie libre les vide (cf.
                    le onChange du champ Adresse) : l'adresse ne vaut donc que si elle a ete choisie
                    dans les suggestions. La rangee `form-row three-cols` qui les portait est retiree
                    et non laissee vide — vide, elle occupait toujours une ligne de la grille. */}

                {/* Présentation de l'organisme — acheteurs uniquement */}
                {isClientRoleLocal(user?.role) && (
                  <>
                    <div className="form-group span-2" style={{ marginTop: '1rem' }}>
                      <label>Type d'organisme<span className="required-asterisk">*</span></label>
                      <select
                        value={profileData.organisme.type_organisme}
                        onChange={e => setProfileData(prev => ({
                          ...prev,
                          organisme: { ...prev.organisme, type_organisme: e.target.value }
                        }))}
                        disabled={!canEditOrganisme()}
                        className={!canEditOrganisme() ? "input-inactif" : ""}
                        style={{ width: '100%' }}
                      >
                        <option value="">-- Sélectionner --</option>
                        <option value="État ou Service de l'État (administration centrale ou déconcentrée)">État ou Service de l'État (administration centrale ou déconcentrée)</option>
                        <option value="Autorité administrative indépendante / autorité publique indépendante">Autorité administrative indépendante / autorité publique indépendante</option>
                        <option value="Commune">Commune</option>
                        <option value="Département">Département</option>
                        <option value="Région">Région</option>
                        <option value="Collectivité territoriale à statut particulier">Collectivité territoriale à statut particulier</option>
                        <option value="Collectivité d'outre-mer">Collectivité d'outre-mer</option>
                        <option value="Établissement public de coopération intercommunale (EPCI)">Établissement public de coopération intercommunale (EPCI)</option>
                        <option value="Syndicat de collectivités territoriales">Syndicat de collectivités territoriales</option>
                        <option value="Autre groupement de collectivités territoriales">Autre groupement de collectivités territoriales</option>
                        <option value="Établissement public administratif (EPA)">Établissement public administratif (EPA)</option>
                        <option value="Établissement public industriel et commercial (EPIC)">Établissement public industriel et commercial (EPIC)</option>
                        <option value="Établissement public local">Établissement public local</option>
                        <option value="Établissement public de santé">Établissement public de santé</option>
                        <option value="Établissement public social ou médico-social">Établissement public social ou médico-social</option>
                        <option value="Établissement public d'enseignement">Établissement public d'enseignement</option>
                        <option value="Établissement public culturel">Établissement public culturel</option>
                        <option value="Office public de l'habitat (OPH)">Office public de l'habitat (OPH)</option>
                        <option value="Établissement public foncier">Établissement public foncier</option>
                        <option value="Établissement public d'aménagement">Établissement public d'aménagement</option>
                        <option value="Centre communal ou intercommunal d'action sociale (CCAS / CIAS)">Centre communal ou intercommunal d'action sociale (CCAS / CIAS)</option>
                        <option value="Caisse des écoles">Caisse des écoles</option>
                        <option value="Organisme de sécurité sociale">Organisme de sécurité sociale</option>
                        <option value="Groupement d'intérêt public (GIP)">Groupement d'intérêt public (GIP)</option>
                        <option value="Groupement de coopération sanitaire (GCS)">Groupement de coopération sanitaire (GCS)</option>
                        <option value="Groupement de coopération sociale ou médico-sociale (GCSMS)">Groupement de coopération sociale ou médico-sociale (GCSMS)</option>
                        <option value="Société publique locale (SPL)">Société publique locale (SPL)</option>
                        <option value="Société publique locale d'aménagement (SPLA)">Société publique locale d'aménagement (SPLA)</option>
                        <option value="Société d'économie mixte (SEM)">Société d'économie mixte (SEM)</option>
                        <option value="Société d'économie mixte à opération unique (SEMOP)">Société d'économie mixte à opération unique (SEMOP)</option>
                        <option value="Fondation ou association">Fondation ou association</option>
                        <option value="Autre organisme">Autre organisme</option>
                      </select>
                    </div>

                    <div className="form-group span-2" style={{ marginTop: '0.5rem' }}>
                      <label>
                        Présentation de l'organisme acheteur<span className="required-asterisk">*</span>
                        <span style={{ float: 'right', fontSize: '0.8rem', color: '#888' }}>
                          {profileData.organisme.presentation_organisme.length} / 5000
                        </span>
                      </label>
                      <textarea
                        value={profileData.organisme.presentation_organisme}
                        onChange={e => setProfileData(prev => ({
                          ...prev,
                          organisme: { ...prev.organisme, presentation_organisme: e.target.value }
                        }))}
                        disabled={!canEditOrganisme()}
                        className={!canEditOrganisme() ? "input-inactif" : ""}
                        rows={8}
                        maxLength={5000}
                        style={{ width: '100%', resize: 'vertical' }}
                        placeholder="Décrivez votre organisme acheteur..."
                      />
                    </div>

                  </>
                )}

                {/* Autosave gère la sauvegarde automatique */}
                {!canEditOrganisme() && (
                  <p className="info-text">
                    {isPrestataireRoleLocal(user?.role)
                      ? "Pour des raisons contractuelles (engagement de 2 ans), les informations de l'entreprise ne peuvent pas être modifiées. Veuillez contacter le support pour toute demande de changement."
                      : "Pour tout changement des informations d'entreprise, contactez l'administrateur de votre compte."
                    }
                  </p>
                )}
            </SectionPliable>

            {/* Section Représentant légal — séparée, acheteurs uniquement */}
            {isClientRoleLocal(user?.role) && (
              <SectionPliable
                title="Représentant légal"
                headerRight={user?.role === 'Acheteur signataire admin' && (
                  <AutoSaveIndicator
                    status={combinedAutoSaveStatus}
                    error={combinedAutoSaveError}
                  />
                )}
              >
                  {/* Représentant N°1 (obligatoire) */}
                  <p className="representant-label"><strong>Représentant N°1</strong><span className="required-asterisk">*</span></p>
                  <div className="form-row three-cols">
                    <div className="form-group">
                      <label>Prénom<span className="required-asterisk">*</span></label>
                      <input
                        type="text"
                        value={profileData.organisme.representant_1_prenom}
                        onChange={e => setProfileData(prev => ({
                          ...prev,
                          organisme: { ...prev.organisme, representant_1_prenom: e.target.value }
                        }))}
                        disabled={!canEditOrganisme()}
                        className={!canEditOrganisme() ? "input-inactif" : ""}
                        placeholder="Prénom"
                      />
                    </div>
                    <div className="form-group">
                      <label>Nom<span className="required-asterisk">*</span></label>
                      <input
                        type="text"
                        value={profileData.organisme.representant_1_nom}
                        onChange={e => setProfileData(prev => ({
                          ...prev,
                          organisme: { ...prev.organisme, representant_1_nom: e.target.value }
                        }))}
                        disabled={!canEditOrganisme()}
                        className={!canEditOrganisme() ? "input-inactif" : ""}
                        placeholder="Nom"
                      />
                    </div>
                    <div className="form-group">
                      <label>Fonction<span className="required-asterisk">*</span></label>
                      <input
                        type="text"
                        value={profileData.organisme.representant_1_fonction}
                        onChange={e => setProfileData(prev => ({
                          ...prev,
                          organisme: { ...prev.organisme, representant_1_fonction: e.target.value }
                        }))}
                        disabled={!canEditOrganisme()}
                        className={!canEditOrganisme() ? "input-inactif" : ""}
                        placeholder="Fonction"
                      />
                    </div>
                  </div>

                  {/* Représentant N°2 (optionnel) */}
                  <p className="representant-label" style={{ marginTop: '0.75rem' }}><strong>Représentant N°2</strong></p>
                  <div className="form-row three-cols">
                    <div className="form-group">
                      <label>Prénom</label>
                      <input
                        type="text"
                        value={profileData.organisme.representant_2_prenom}
                        onChange={e => setProfileData(prev => ({
                          ...prev,
                          organisme: { ...prev.organisme, representant_2_prenom: e.target.value }
                        }))}
                        disabled={!canEditOrganisme()}
                        className={!canEditOrganisme() ? "input-inactif" : ""}
                        placeholder="Prénom"
                      />
                    </div>
                    <div className="form-group">
                      <label>Nom</label>
                      <input
                        type="text"
                        value={profileData.organisme.representant_2_nom}
                        onChange={e => setProfileData(prev => ({
                          ...prev,
                          organisme: { ...prev.organisme, representant_2_nom: e.target.value }
                        }))}
                        disabled={!canEditOrganisme()}
                        className={!canEditOrganisme() ? "input-inactif" : ""}
                        placeholder="Nom"
                      />
                    </div>
                    <div className="form-group">
                      <label>Fonction</label>
                      <input
                        type="text"
                        value={profileData.organisme.representant_2_fonction}
                        onChange={e => setProfileData(prev => ({
                          ...prev,
                          organisme: { ...prev.organisme, representant_2_fonction: e.target.value }
                        }))}
                        disabled={!canEditOrganisme()}
                        className={!canEditOrganisme() ? "input-inactif" : ""}
                        placeholder="Fonction"
                      />
                    </div>
                  </div>

                  {/* Représentant N°3 (optionnel) */}
                  <p className="representant-label" style={{ marginTop: '0.75rem' }}><strong>Représentant N°3</strong></p>
                  <div className="form-row three-cols">
                    <div className="form-group">
                      <label>Prénom</label>
                      <input
                        type="text"
                        value={profileData.organisme.representant_3_prenom}
                        onChange={e => setProfileData(prev => ({
                          ...prev,
                          organisme: { ...prev.organisme, representant_3_prenom: e.target.value }
                        }))}
                        disabled={!canEditOrganisme()}
                        className={!canEditOrganisme() ? "input-inactif" : ""}
                        placeholder="Prénom"
                      />
                    </div>
                    <div className="form-group">
                      <label>Nom</label>
                      <input
                        type="text"
                        value={profileData.organisme.representant_3_nom}
                        onChange={e => setProfileData(prev => ({
                          ...prev,
                          organisme: { ...prev.organisme, representant_3_nom: e.target.value }
                        }))}
                        disabled={!canEditOrganisme()}
                        className={!canEditOrganisme() ? "input-inactif" : ""}
                        placeholder="Nom"
                      />
                    </div>
                    <div className="form-group">
                      <label>Fonction</label>
                      <input
                        type="text"
                        value={profileData.organisme.representant_3_fonction}
                        onChange={e => setProfileData(prev => ({
                          ...prev,
                          organisme: { ...prev.organisme, representant_3_fonction: e.target.value }
                        }))}
                        disabled={!canEditOrganisme()}
                        className={!canEditOrganisme() ? "input-inactif" : ""}
                        placeholder="Fonction"
                      />
                    </div>
                  </div>

              </SectionPliable>
            )}

            {/* Section Périmètre géographique d'achat — section pliable dédiée, acheteurs uniquement */}
            {isClientRoleLocal(user?.role) && (
              <SectionPliable title="Périmètre géographique d'achat">
                <PerimetreSelector
                  value={perimetreDetail}
                  onChange={setPerimetreDetail}
                  disabled={!canEditOrganisme()}
                  saving={autoSavePerimetre.status === 'saving'}
                />
                <p className="perimetre-section-hint">
                  Définit où votre organisme passe commande. National, ou des régions entières et/ou des départements précis.
                </p>
              </SectionPliable>
            )}
          </>
        )}
      </section>


    </>
  );

  // Onglet Gestion d'équipe - Affiche 2 sections pour prestataires, 1 section pour acheteurs
  const renderListeCoordonnees = () => {
    const isPrestataire = isPrestataireRoleLocal(user?.role);

    if (isPrestataire) {
      // Pour les prestataires : 2 sections (Affectation + Membres de l'équipe)
      return (

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Section 2: Membres de l'équipe */}
          <SectionPliable title="Membres de l'équipe">
              <React.Suspense fallback={<div>Chargement...</div>}>
                <GestionComptesPrestataires />
              </React.Suspense>
          </SectionPliable>


          {/* Section 1: Affectation des membres sur lot remportés */}
          <SectionPliable title="Affectation des membres sur les lots remportés">
              <GestionComptes />
          </SectionPliable>


        </div>
      );
    } else {
      // Pour les acheteurs : 1 seule section (Membres de l'équipe avec gestion des comptes)
      return (
        <SectionPliable title="Membres de l'équipe">
            <React.Suspense fallback={<div>Chargement...</div>}>
              <GestionComptes userType="acheteur" />
            </React.Suspense>
        </SectionPliable>
      );
    }
  };

  // Onglet droits utilisateur
  const renderDroitsUtilisateur = () => (
    <ChangeRole organismeId={Number(profileData.organisme.id) || 0} />
  );

  // Onglet mes documents
  const renderMesDocuments = () => (
    <div className="profil-documents-page">
      <MesDocuments />
    </div>
  );

  // Onglet gestion des comptes - RENOMMÉ en "Mes interlocuteurs" pour Prestataire signataire admin
  const renderMesInterlocuteurs = () => {
    return <GestionComptes />;
  };

  // Déterminer quels onglets sont visibles selon le rôle - MÉMOÏSÉ
  const getVisibleTabs = React.useMemo(() => {
    const userRole = user?.role;
    // console.log(userRole);

    // Onglets de base disponibles pour tous les utilisateurs
    const baseTabs = ["Présentation"];

    // Ajouter l'onglet "Documents" pour les acheteurs (position 2, juste après Présentation)
    if (isClientRoleLocal(userRole)) {
      baseTabs.push("Documents");
    }

    // Ajouter l'onglet "Droits" pour tous les acheteurs
    if (isClientRoleLocal(userRole)) {
      baseTabs.push("Droits");
    }

    // Ajouter l'onglet "Gestion d'équipe" pour tous les acheteurs
    if (isClientRoleLocal(userRole)) {
      baseTabs.push("Gestion d'équipe");
    }

    // Ajouter l'onglet "Mes documents" pour tous les prestataires
    if (isPrestataireRoleLocal(userRole)) {
      baseTabs.push("Documents");
    }

    // Ajouter "Gestion d'équipe" pour tous les prestataires
    if (isPrestataireRoleLocal(userRole)) {
      baseTabs.push("Gestion d'équipe");
    }

    // NOTE: L'onglet "Mes interlocuteurs" a été fusionné avec "Gestion d'équipe"
    // Il n'est plus nécessaire en tant que tab séparée

    // Ajouter l'onglet "Statistiques et besoins" pour les rôles clients
    // (ex-"Mon besoin", fusionne l'ancien onglet "Statistiques")
    if (isClientRoleLocal(userRole)) {
      baseTabs.push("Statistiques et besoins");
    }

    // Ajouter l'onglet "Évaluations" pour les acheteurs.
    // (Profil.tsx ne sert QUE les acheteurs ; les prestataires passent par ProfilUnifie
    // via ProfilRouter, qui a son propre onglet "Évaluations".)
    if (isClientRoleLocal(userRole)) {
      baseTabs.push("Évaluations");
    }

    // Onglet "Statistiques" commente : fusionne dans "Statistiques et besoins"
    // baseTabs.push("Statistiques");

    return baseTabs;
  }, [user?.role, isClientRoleLocal, isPrestataireRoleLocal]);

  // Obtenir les onglets visibles
  const visibleTabs = getVisibleTabs;

  // Charge la synthese besoin a l'ouverture de l'onglet. Une seule fois tant que
  // le cache est rempli ; il est remis a null quand le perimetre change (cf. le
  // contrat de fraicheur documente sur l'etat syntheseBesoin), ce qui redeclenche
  // cet effet et donc un refetch a jour.
  useEffect(() => {
    if (visibleTabs[activeTab] !== 'Statistiques et besoins' || syntheseBesoin) {
      return;
    }
    let cancelled = false;
    setSyntheseBesoinLoading(true);
    apiService.getSyntheseBesoin()
      .then((data: SyntheseBesoinData | null) => {
        if (!cancelled) setSyntheseBesoin(data);
      })
      .catch(() => {
        if (!cancelled) setSyntheseBesoin(null);
      })
      .finally(() => {
        if (!cancelled) setSyntheseBesoinLoading(false);
      });
    return () => { cancelled = true; };
  }, [visibleTabs, activeTab, syntheseBesoin]);

  // Charge l'evaluation Centralis a l'ouverture de l'onglet (une seule fois)
  useEffect(() => {
    if (visibleTabs[activeTab] !== 'Évaluations' || evaluationCentralis) {
      return;
    }
    let cancelled = false;
    setEvaluationCentralisLoading(true);
    apiService.getEvaluationCentralis()
      .then((data: EvaluationCentralisData | null) => {
        if (!cancelled) setEvaluationCentralis(data);
      })
      .catch(() => {
        if (!cancelled) setEvaluationCentralis(null);
      })
      .finally(() => {
        if (!cancelled) setEvaluationCentralisLoading(false);
      });
    return () => { cancelled = true; };
  }, [visibleTabs, activeTab, evaluationCentralis]);

  // Avis deposes : effet SEPARE (son propre `cancelled`). Couple au fetch centralis, le
  // changement d'etat `evaluationCentralis` re-declenchait l'effet et annulait ce fetch en vol
  // (reponse recue mais setState ignore -> liste vide). Decouple = plus de course.
  useEffect(() => {
    if (visibleTabs[activeTab] !== 'Évaluations' || avisDonnesLoaded) {
      return;
    }
    let cancelled = false;
    apiService.getAvisPrestataireDonnes()
      .then((d: AvisData[]) => {
        if (!cancelled) { setAvisDonnes(Array.isArray(d) ? d : []); setAvisDonnesLoaded(true); }
      })
      .catch(() => { if (!cancelled) setAvisDonnesLoaded(true); });
    return () => { cancelled = true; };
  }, [visibleTabs, activeTab, avisDonnesLoaded]);

  // Avis RECUS des prestataires (PRIVE) : effet separe, meme onglet Évaluations.
  useEffect(() => {
    if (visibleTabs[activeTab] !== 'Évaluations' || avisAcheteurRecusLoaded) {
      return;
    }
    let cancelled = false;
    apiService.getAvisAcheteurRecus()
      .then((res: { avis?: AvisData[]; moyenne?: number | null } | null) => {
        if (!cancelled) {
          setAvisAcheteurRecus(Array.isArray(res?.avis) ? (res!.avis as AvisData[]) : []);
          setAvisAcheteurMoyenne(res?.moyenne ?? null);
          setAvisAcheteurRecusLoaded(true);
        }
      })
      .catch(() => { if (!cancelled) setAvisAcheteurRecusLoaded(true); });
    return () => { cancelled = true; };
  }, [visibleTabs, activeTab, avisAcheteurRecusLoaded]);

  const getAcheteurTabsWithIssues = useCallback((): number[] => {
    if (!acheteurCompletion) return [];
    const issues: number[] = [];
    if (!acheteurCompletion.sections.presentation) issues.push(0);
    // Logo et habilitation sont tous deux dans l'onglet Documents et obligatoires.
    if (!acheteurCompletion.sections.logo || !acheteurCompletion.sections.documents) {
      const docIdx = visibleTabs.indexOf('Documents');
      if (docIdx !== -1) issues.push(docIdx);
    }
    return issues;
  }, [acheteurCompletion, visibleTabs]);

  // Synchroniser l'onglet actif avec l'URL a CHAQUE changement de ?tab= (pas seulement au montage) :
  // 1) depuis l'URL (?tab=, persistance generale -> on reste sur l'onglet au rechargement, ET le menu
  //    deroulant qui pointe vers /profil?tab=X change bien d'onglet quand on est deja sur /profil) ;
  // 2) sinon localStorage (flux ponctuel de reattribution, declenche au montage sans ?tab=).
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab) {
      const idx = visibleTabs.indexOf(urlTab);
      if (idx !== -1) { setActiveTab(idx); return; }
    }
    const savedTab = localStorage.getItem('activeProfilTab');
    if (savedTab) {
      const tabIndex = visibleTabs.indexOf(savedTab);
      if (tabIndex !== -1) {
        setActiveTab(tabIndex);
      }
      // Nettoyer le localStorage après avoir restauré l'onglet
      localStorage.removeItem('activeProfilTab');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, visibleTabs]);

  // Déterminer quels onglets doivent être inactifs - MÉMOÏSÉ
  const getInactiveTabs = React.useMemo(() => {
    const inactiveTabs: number[] = [];

    // L'onglet "Statistiques et besoins" (ex-"Mon besoin") est desormais actif
    // const statsEtBesoinsIndex = visibleTabs.indexOf("Statistiques et besoins");
    // if (statsEtBesoinsIndex !== -1) {
    //   inactiveTabs.push(statsEtBesoinsIndex);
    // }

    // L'onglet "Évaluations" (ex-"Mes évaluations") est desormais actif (section Centralis branchee)

    // Onglet "Statistiques" commente : fusionne dans "Statistiques et besoins"
    // const statsIndex = visibleTabs.indexOf("Statistiques");
    // if (statsIndex !== -1) {
    //   inactiveTabs.push(statsIndex);
    // }

    return inactiveTabs;
  }, [visibleTabs]);

  // Obtenir les onglets inactifs
  const inactiveTabs = getInactiveTabs;

  // Gérer le changement d'onglet avec index ajustés
  const handleTabChange = (tabIdx: number) => {
    // Avant d'ouvrir Statistiques et besoins : flush un eventuel changement de
    // perimetre en attente (debounce). Le saveFn invalide alors le cache synthese,
    // garantissant un affichage a jour a l'arrivee sur l'onglet.
    if (getVisibleTabs[tabIdx] === 'Statistiques et besoins') {
      autoSavePerimetre.flush();
    }
    // Stocker l'index réel dans le state
    setActiveTab(tabIdx);
    setActiveDocTab(null);
    // Persiste l'onglet dans l'URL (?tab=) -> on y reste au rechargement.
    const name = getVisibleTabs[tabIdx];
    if (name) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', name);
      setSearchParams(next, { replace: true });
    }
  };

  const renderStatistiquesEtBesoins = () => {
    const sb = syntheseBesoin;
    const rep = sb?.repartition;
    // Banniere "Derniere mise a jour" : PERMANENTE (toujours affichee). Message par defaut
    // tant qu'aucune modification des besoins n'a ete enregistree.
    const fmtMaj = (m: DerniereMaj | null): string => {
      if (!m?.date) return 'Aucune mise à jour enregistrée pour le moment';
      const d = new Date(m.date);
      const date = d.toLocaleDateString('fr-FR');
      const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      return `Dernière mise à jour le ${date} à ${heure} par ${m.par ?? '—'}`;
    };
    const majMessage = fmtMaj(derniereMaj);
    // null/undefined -> tiret ; 0 est une valeur valide a afficher.
    const fmtMontant = (n?: number) => (n == null ? '—' : `${Math.round(n).toLocaleString('fr-FR')}€`);
    const fmtNombre = (n?: number) => (n == null ? '—' : String(n));
    // Evolution : signe explicite, '—' si pas de base de comparaison (annee precedente a 0).
    const fmtEvolution = (n?: number | null) => (n == null ? '—' : `${n > 0 ? '+' : ''}${n}%`);

    // Perimetre : resume concis pour la carte (le detail riche est affiche plus bas).
    const pdet = sb?.perimetre_detail;
    const perimetreResume = (() => {
      if (pdet?.type === 'national') return 'National';
      if (pdet?.type === 'zones') {
        const nbReg = pdet.regions.length;
        const nbDep = pdet.departements.length;
        if (nbReg === 0 && nbDep === 0) return '—';
        const parts: string[] = [];
        if (nbReg) parts.push(`${nbReg} région${nbReg > 1 ? 's' : ''}`);
        if (nbDep) parts.push(`${nbDep} dép.`);
        return parts.join(' · ');
      }
      return sb?.perimetre ?? '—';
    })();

    // Cartes Budget : en dur pour l'instant (hors scope de la route).
    // Cartes dynamic : branchees sur GET /synthese-besoin (commandes validees, annee, organisme),
    // avec skeleton pendant le chargement (cf. rendu plus bas). La carte "Périmètre"
    // est rendue à part (PerimetreStatCard, avec tooltip de detail au survol).
    const repP = sb?.repartition_precedent;
    const usrP = sb?.utilisateurs_precedent;
    // valeurN1 : meme metrique pour l'annee precedente (affichee en gris sous le chiffre).
    const syntheseCards = [
      // Budget = consomme reel de l'annee (live BC/MS) / total financier previsionnel (tableau).
      { titre: 'Budget', valeur: `${fmtMontant(sb?.montant_total)} / ${fmtMontant(sb?.montant_total_previsionnel)}`,
        valeurN1: sb?.montant_total_precedent != null ? `N-1 : ${fmtMontant(sb.montant_total_precedent)}` : undefined,
        icon: iconBudget, dynamic: true },
      { titre: 'Montant moyen', valeur: fmtMontant(sb?.montant_moyen),
        valeurN1: sb?.montant_moyen_precedent != null ? `N-1 : ${fmtMontant(sb.montant_moyen_precedent)}` : undefined,
        icon: iconAmount, dynamic: true },
      { titre: 'Évolution financière', valeur: fmtEvolution(sb?.evolution_financiere),
        // Toujours affichee (— s'il n'y a pas de base N-2), comme les autres cartes.
        valeurN1: `N-1 : ${fmtEvolution(sb?.evolution_financiere_precedent)}`,
        icon: iconTrendingUp, dynamic: true },
      { titre: 'Nombre de commande', valeur: fmtNombre(sb?.nombre_commandes),
        valeurN1: sb?.nombre_commandes_precedent != null ? `N-1 : ${fmtNombre(sb.nombre_commandes_precedent)}` : undefined,
        icon: iconOrders, dynamic: true },
      { titre: 'Répartition moyenne', valeur: rep ? `${rep.MS ?? 0} (MS) / ${rep.BC ?? 0} (BC)` : '—',
        valeurN1: repP ? `N-1 : ${repP.MS ?? 0} (MS) / ${repP.BC ?? 0} (BC)` : undefined,
        icon: iconDistribution, dynamic: true },
      { titre: 'Utilisateurs actifs', valeur: sb?.utilisateurs ? `${sb.utilisateurs.actifs}/${sb.utilisateurs.total}` : '—',
        valeurN1: usrP ? `N-1 : ${usrP.actifs}/${usrP.total}` : undefined,
        icon: iconActiveUsers, dynamic: true },
    ];

    return (
      <div className="profil-tab-page stats-besoins-page">
        {/* Bandeau de derniere mise a jour : PERMANENT (haut de page). */}
        <Banner variant="success" filled hideIcon message={majMessage} />

        {/* Section bandeau gris pliable : synthese annuelle - 7 cartes sur une ligne */}
        <SectionPliable title="Synthèse annuelle">
          <Banner variant="info" message="Les chiffres correspondent à l'année civile en cours." />
          <div className="synthese-cards">
            {/* Carte Périmètre : détail complet en tooltip au survol (périmètre géographique d'achat). */}
            <PerimetreStatCard
              valeur={perimetreResume}
              detail={pdet}
              fallback={sb?.perimetre}
              icon={iconLocation}
              loading={syntheseBesoinLoading}
            />
            {syntheseCards.map((carte) => (
              <div key={carte.titre} className="synthese-card">
                <img className="synthese-card-icon" src={carte.icon} alt="" aria-hidden="true" />
                <span className="synthese-card-titre">{carte.titre}</span>
                {carte.dynamic && syntheseBesoinLoading ? (
                  <>
                    <span className="synthese-card-skeleton" aria-hidden="true" />
                    <span className="synthese-card-skeleton synthese-card-skeleton-n1" aria-hidden="true" />
                  </>
                ) : (
                  <>
                    <span className="synthese-card-valeur">{carte.valeur}</span>
                    {carte.valeurN1 && <span className="synthese-card-valeur-n1">{carte.valeurN1}</span>}
                  </>
                )}
              </div>
            ))}
          </div>
        </SectionPliable>

        <EstimationBesoins onMajChange={handleMajChange} />

        <LocalisationBesoins onMajChange={handleMajChange} onPerimetreChange={handlePerimetreFromStats} />

        {/* Banniere "Derniere mise a jour" PERMANENTE, dedoublee en bas de page. */}
        <Banner variant="success" filled hideIcon message={majMessage} />

        {/* Telechargements CSV : alignes a droite, sous la derniere banniere (bas de page). */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'flex-end', margin: '1rem 0' }}>
          <Button variant="secondary" value="Télécharger les statistiques et besoins" onClick={handleExportStats} />
          <Button variant="secondary" value="Télécharger l'historique des commandes" onClick={handleExportCommandes} />
        </div>
      </div>
    );
  };

  // Clic d'etoile : mise a jour optimiste de mes notes, puis upsert serveur (autoritaire).
  const handleRateCentralis = useCallback((critere: EvalCritere, note: number) => {
    setEvaluationCentralis((prev) =>
      prev ? { ...prev, notes: { ...prev.notes, [critere]: note } } : prev
    );
    apiService.rateEvaluationCentralis(critere, note)
      .then((data: EvaluationCentralisData | null) => {
        if (data) setEvaluationCentralis(data);
      })
      .catch(() => {
        // Echec : resynchronise sur l'etat serveur reel.
        apiService.getEvaluationCentralis()
          .then((data: EvaluationCentralisData | null) => setEvaluationCentralis(data))
          .catch(() => undefined);
      });
  }, []);

  const renderEvaluations = () => {
    // Profil.tsx ne sert que les acheteurs (cf. ProfilRouter) -> cote acheteur.
    return (
      <>
        <EvaluationCentralis
          cote="acheteur"
          data={evaluationCentralis}
          loading={evaluationCentralisLoading}
          onRate={handleRateCentralis}
        />
        <div className="profil-tab-page evaluations-page">
          <SectionPliable title="Avis des acheteurs concernant les prestataires">
            <Banner
              variant="info"
              className="eval-notice"
              message="Vous retrouverez ci-dessous tous les avis que les utilisateurs de votre compte acheteurs ont émis par commande réalisée. Vous pouvez modifier vos avis à tout moment dans l’onglet Évaluation de l’opération concernée."
            />
            <AvisGroupes
              avis={avisDonnes}
              groupBy="prestataire"
              showButton
              loading={!avisDonnesLoaded}
              emptyMessage="Vous n'avez encore évalué aucun prestataire. Les avis se déposent depuis l'onglet « Évaluation » d'une commande validée."
            />
            {/* Export PDF de l'ensemble des avis deposes (bas a droite de la section). */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.2rem' }}>
              <Button
                variant="secondary"
                value="Télécharger les avis donnés"
                icon={<PdfIcon />}
                onClick={handleExportAvisPdf}
                disabled={!avisDonnesLoaded || avisDonnes.length === 0}
              />
            </div>
          </SectionPliable>
          <SectionPliable title="Avis des prestataires concernant votre structure">
            <Banner
              variant="info"
              className="eval-notice"
              message="Vous retrouverez ci-dessous les avis que les prestataires ont émis sur votre structure, par commande réalisée. Ces informations restent privées : seuls vous et le prestataire concerné y ont accès."
            />
            <AvisGroupes
              avis={avisAcheteurRecus}
              groupBy="prestataire"
              average={avisAcheteurMoyenne}
              showButton
              loading={!avisAcheteurRecusLoaded}
              emptyMessage="Aucun avis pour le moment. Les prestataires déposent leurs avis depuis l'onglet « Évaluation » de leurs commandes validées."
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.2rem' }}>
              <Button
                variant="secondary"
                value="Télécharger les avis reçus"
                icon={<PdfIcon />}
                onClick={handleExportAvisAcheteurPdf}
                disabled={!avisAcheteurRecusLoaded || avisAcheteurRecus.length === 0}
              />
            </div>
          </SectionPliable>
          {/* Bandeau vert "Derniere mise a jour", a la TOUTE FIN de la page. */}
          <EvaluationLastUpdateBanner data={evaluationCentralis} />
        </div>
      </>
    );
  };

  // Déterminer quel contenu afficher selon l'onglet actif - MÉMOÏSÉ
  const renderTabContent = React.useMemo(() => {
    if (loading) return <ProfilSkeleton sections={2} />;
    switch (visibleTabs[activeTab]) {
      case "Présentation":
        return renderCoordonnees();
      case "Documents":
        if (isClientRoleLocal(user?.role)) {
          return <div className="profil-documents-page"><TabDocumentsAcheteur /></div>;
        }
        return renderMesDocuments();
      case "Droits":
        return renderDroitsUtilisateur();
      case "Gestion d'équipe":
        return renderListeCoordonnees(); // Affiche maintenant GestionComptes
      case "Statistiques et besoins":
        return renderStatistiquesEtBesoins();
      case "Évaluations":
        return renderEvaluations();
      default:
        return null;
    }
  }, [visibleTabs, activeTab, profileData, loading, error, phoneError, user, showModificationModal, modificationData, combinedAutoSaveStatus, combinedAutoSaveError, syntheseBesoin, syntheseBesoinLoading, evaluationCentralis, evaluationCentralisLoading, avisDonnes, avisDonnesLoaded, avisAcheteurRecus, avisAcheteurMoyenne, avisAcheteurRecusLoaded, derniereMaj, perimetreDetail, autoSavePerimetre.status]);

  // Onglet "Statistiques et besoins" desormais actif : plus de tooltip d'onglet inactif
  // const monBesoinMessage = "Cette fonctionnalité sera disponible prochainement";

  // Page principale avec onglets toujours visibles
  return (
    <div className="profil-container">
      {organismeToast && (
        <AlertMessage
          type={organismeToast.type}
          message={organismeToast.message}
          onClose={() => setOrganismeToast(null)}
        />
      )}
      {/* Progression (% + pastille + barre) a droite de la ligne d'onglets ; plus de gros titre "Profil". */}
      <div className="profil-tabs">
        <TabNav
          tabs={visibleTabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          inactiveTabs={inactiveTabs}
          inactiveTabsTooltips={{}}
          notifications={getAcheteurTabsWithIssues().map(idx => ({ tabIndex: idx, count: 1 }))}
          rightContent={isClientRoleLocal(user?.role) && acheteurCompletion && (() => {
            // Statut couleur : vert si complet, orange si entame (>0), rouge si a 0.
            const pct = acheteurCompletion.percent;
            const statutClass = acheteurCompletion.is_complete ? 'is-complete' : pct > 0 ? 'is-partial' : 'is-incomplete';
            const barColor = acheteurCompletion.is_complete ? '#22c55e' : pct > 0 ? '#f97316' : '#ef4444';
            return (
              <div className="profil-header-completion">
                <div className="profil-header-completion-top">
                  <span className={`profil-header-percent ${statutClass}`}>{pct}%</span>
                  <span className={`profil-header-pastille ${statutClass}`}>
                    {acheteurCompletion.is_complete ? 'Complet' : 'Incomplet'}
                  </span>
                </div>
                <div className="profil-header-bar">
                  <div
                    className="profil-header-bar-fill"
                    style={{ width: `${pct}%`, backgroundColor: barColor }}
                  />
                </div>
              </div>
            );
          })()}
        />
      </div>
      {renderTabContent}
      <ModificationModal
        showModal={showModificationModal}
        data={modificationData}
        onChange={handleModificationChange}
        onClose={() => setShowModificationModal(false)}
        onSubmit={handleSubmitModification}
        userRole={user?.role}
        phoneError={phoneError}
        setPhoneError={setPhoneError}
        validatePhoneNumber={validatePhoneNumber} // Ajout de cette prop
      />
    </div>
  );
};

export default Profil;